import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchPositions, createPosition, updatePosition, fetchDepartments } from '../../../services/employee';
import CustomPagination from '../../../components/common/CustomPagination.js';

const Positions = () => {
  const { t } = useTranslation(['common', 'employees']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    code: '',
    title: '',
    description: '',
    department: '',
    level: 'entry',
    employment_type: 'full_time',
    min_salary: '',
    max_salary: '',
    requirements: '',
    responsibilities: '',
    benefits: '',
    is_active: true
  });
  const [departments, setDepartments] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchPositions(params);
      
      if (response.results) {
        setPositions(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setPositions(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch positions');
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch departments for dropdown when modal opens
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [deptsRes] = await Promise.all([
          fetchDepartments({ page_size: 1000 }).catch(() => ({ results: [] }))
        ]);

        setDepartments(deptsRes.results || deptsRes || []);
      } catch (err) {
        console.error('Failed to fetch dropdown data:', err);
      }
    };

    if (modalOpen) {
      fetchDropdownData();
    }
  }, [modalOpen]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (position = null) => {
    if (position) {
      setEditId(position.id);
      setForm({
        code: position.code || '',
        title: position.title || '',
        description: position.description || '',
        department: position.department?.id || position.department || '',
        level: position.level || 'entry',
        employment_type: position.employment_type || 'full_time',
        min_salary: position.min_salary || '',
        max_salary: position.max_salary || '',
        requirements: position.requirements || '',
        responsibilities: position.responsibilities || '',
        benefits: position.benefits || '',
        is_active: position.is_active !== undefined ? position.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        code: '',
        title: '',
        description: '',
        department: '',
        level: 'entry',
        employment_type: 'full_time',
        min_salary: '',
        max_salary: '',
        requirements: '',
        responsibilities: '',
        benefits: '',
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      code: '',
      title: '',
      description: '',
      department: '',
      level: 'entry',
      employment_type: 'full_time',
      min_salary: '',
      max_salary: '',
      requirements: '',
      responsibilities: '',
      benefits: '',
      is_active: true
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!form.title || !form.code) {
      setSnackbar({ open: true, message: 'Title and Code are required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code,
        title: form.title,
        description: form.description || null,
        department: form.department || null,
        level: form.level,
        employment_type: form.employment_type,
        min_salary: form.min_salary ? parseFloat(form.min_salary) : null,
        max_salary: form.max_salary ? parseFloat(form.max_salary) : null,
        requirements: form.requirements || null,
        responsibilities: form.responsibilities || null,
        benefits: form.benefits || null,
        is_active: form.is_active
      };

      if (editId) {
        await updatePosition(editId, payload);
        setSnackbar({ open: true, message: 'Position updated successfully', severity: 'success' });
      } else {
        await createPosition(payload);
        setSnackbar({ open: true, message: 'Position created successfully', severity: 'success' });
      }

      handleCloseModal();
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save position',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return positions.map((pos, index) => ({
      ...pos,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [positions, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('employees:positions.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'code',
      headerName: t('employees:positions.columns.code'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'title',
      headerName: t('employees:positions.columns.title'),
      flex: 1,
      minWidth: 200,
      sortable: false
    },
    {
      field: 'department_name',
      headerName: t('employees:positions.columns.department'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'level',
      headerName: t('employees:positions.columns.level'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const level = params.value || '';
        return level.charAt(0).toUpperCase() + level.slice(1).replace('_', ' ');
      }
    },
    {
      field: 'employment_type',
      headerName: t('employees:positions.columns.type'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const type = params.value || '';
        return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
      }
    },
    {
      field: 'current_employees_count',
      headerName: t('employees:positions.columns.employees'),
      width: 100,
      sortable: false,
      renderCell: (params) => params.value || 0
    },
    {
      field: 'is_active',
      headerName: t('employees:positions.columns.status'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employees:active') : t('employees:inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: t('employees:positions.columns.actions'),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('common:buttons.edit')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenModal(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ], [t]);

  if (loading && positions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:positions.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('employees:positions.addPosition')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:search')}
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('employees:positions.searchPlaceholder')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:department')}
                fullWidth
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                placeholder={t('employees:department') + ' ID'}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="true">{t('employees:active')}</option>
                <option value="false">{t('employees:inactive')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Position Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? t('employees:positions.editPosition') : t('employees:positions.addPosition')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Basic Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#DC143C' }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Position Code"
                  value={form.code}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  helperText="Unique code for the position"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Position Title"
                  value={form.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Department and Level */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  Department & Level
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => `${option.name || ''} (${option.code || ''})`}
                  value={departments.find(d => d.id === form.department) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('department', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department"
                      placeholder="Select department"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Level"
                  value={form.level}
                  onChange={(e) => handleFormChange('level', e.target.value)}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="entry">Entry Level</option>
                  <option value="junior">Junior</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                  <option value="manager">Manager</option>
                  <option value="director">Director</option>
                  <option value="executive">Executive</option>
                </TextField>
              </Grid>

              {/* Employment Details */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  Employment Details
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Employment Type"
                  value={form.employment_type}
                  onChange={(e) => handleFormChange('employment_type', e.target.value)}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="intern">Intern</option>
                  <option value="consultant">Consultant</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Min Salary"
                  type="number"
                  value={form.min_salary}
                  onChange={(e) => handleFormChange('min_salary', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Max Salary"
                  type="number"
                  value={form.max_salary}
                  onChange={(e) => handleFormChange('max_salary', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              {/* Additional Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  Additional Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Requirements"
                  value={form.requirements}
                  onChange={(e) => handleFormChange('requirements', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                  placeholder="List the requirements for this position..."
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Responsibilities"
                  value={form.responsibilities}
                  onChange={(e) => handleFormChange('responsibilities', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                  placeholder="List the responsibilities for this position..."
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Benefits"
                  value={form.benefits}
                  onChange={(e) => handleFormChange('benefits', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                  placeholder="List the benefits for this position..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Positions;

